import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { GTDialog, GTDialogBody, GTDialogFooter, GTDialogHeading, GTDialogSteps } from '@/components/ui/gt-dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { ArrowRight, CalendarRange, Gauge } from 'lucide-react'
import { useGoalCreation } from '../shared/GoalCreationContext'
import PlanDraftingState from '../shared/PlanDraftingState'
import PropertyPill, {
    CAPACITY_OPTIONS,
    CAPACITY_UNITS,
    NO_CAPACITY_LABEL,
    NO_TIMEFRAME_LABEL,
    TIMEFRAME_OPTIONS,
    capacityFromLabel,
} from '../shared/PropertyPill'
import ReviewScreen from '../shared/ReviewScreen'
import StartingPointPanel from '../shared/StartingPointPanel'
import { TGoalDraft, TScenarioPath } from '../shared/scenario'
import { TGoalStartingPoint, creationCommitmentsWithReuse } from '../shared/startingPoint'
import {
    GOAL_DRAFT_UNCHANGED_MESSAGE,
    TGoalDraftRevision,
    TScaffoldDraftInput,
    buildScaffoldReviewDraft,
    draftGoalPaths,
} from './scaffoldDraft'

type TStep = 1 | 2 | 3 | 4 | 5

const OUTCOME_PREFILL = ''
const WHY_PREFILL = ''
const DEFAULT_TIMEFRAME_LABEL = NO_TIMEFRAME_LABEL
const DEFAULT_CAPACITY_LABEL = NO_CAPACITY_LABEL

const StepHeading = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div
        style={{ animationDelay: '0ms', animationFillMode: 'both' }}
        className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
    >
        <GTDialogHeading title={title} subtitle={subtitle} />
    </div>
)

/**
 * Iteration 2 — The Scaffold. A five-step guided stepper (Outcome → Why →
 * Timeframe & capacity → Starting point → Plan) that lands on the same ReviewScreen the Coach
 * flow ends with. No chat surface anywhere. Lives in the standard GTDialog shell
 * (step indicator in the header's meta slot) in normal mode, or renders the same
 * body/footer inline inside `OnboardingFrame` (isOnboarding) — GoalCreationFlows supplies that frame.
 */
const ScaffoldFlow = () => {
    const { isOnboarding, closeFlow, createGoal } = useGoalCreation()

    const [render, setRender] = useState<TStep>(1)
    const [exiting, setExiting] = useState(false)
    const busyRef = useRef(false)

    const [outcome, setOutcome] = useState(OUTCOME_PREFILL)
    const [why, setWhy] = useState(WHY_PREFILL)
    const [timeframeLabel, setTimeframeLabel] = useState(DEFAULT_TIMEFRAME_LABEL)
    const [capacityLabel, setCapacityLabel] = useState(DEFAULT_CAPACITY_LABEL)
    const [startingPoint, setStartingPoint] = useState<TGoalStartingPoint | undefined>()
    const [draftPaths, setDraftPaths] = useState<TScenarioPath[]>([])
    const [isDrafting, setIsDrafting] = useState(false)
    // A revision is a distinct request: it re-fires the draft without any of the four goal
    // fields changing (each note is a fresh object, so the effect runs again even for the same
    // words), and it is never served from the pre-feedback cache.
    const [revision, setRevision] = useState<TGoalDraftRevision | null>(null)
    // ReviewScreen remounts when a revised plan arrives. Goal hooks are review edits too,
    // so lift them alongside the title/properties instead of resetting them on success.
    const [reviewRecurringTemplateIds, setReviewRecurringTemplateIds] = useState<string[] | undefined>()
    // Bumped only when a plan actually arrives. ReviewScreen keys off it, so a revision that
    // fails leaves the plan — and the rows the user had already edited — exactly where they were.
    const [planGeneration, setPlanGeneration] = useState(0)
    const draftCache = useRef<{ key: string; paths: TScenarioPath[] } | null>(null)
    const openPopovers = useRef(0)

    const inputRef = useRef<HTMLInputElement>(null)
    const whyRef = useRef<HTMLTextAreaElement>(null)

    const goTo = useCallback(
        (next: TStep) => {
            if (next < 1 || next > 5 || next === render || busyRef.current) return
            busyRef.current = true
            setExiting(true)
            window.setTimeout(() => {
                setRender(next)
                setExiting(false)
                busyRef.current = false
            }, 150)
        },
        [render]
    )

    const advance = useCallback(() => {
        if (render === 1 && outcome.trim().length === 0) return
        if (render < 4) goTo((render + 1) as TStep)
    }, [render, outcome, goTo])

    const back = useCallback(() => {
        if (render > 1) goTo((render - 1) as TStep)
    }, [render, goTo])

    useEffect(() => {
        if (render !== 5) {
            setDraftPaths([])
            setIsDrafting(false)
            setRevision(null)
            if (render <= 3) {
                setStartingPoint(undefined)
                setReviewRecurringTemplateIds(undefined)
            }
            return
        }
        let canceled = false
        const controller = new AbortController()
        const draftInput: TScaffoldDraftInput = {
            title: outcome,
            why,
            timeframeLabel,
            capacityLabel,
            startingPoint,
        }
        const draftKey = JSON.stringify({
            title: draftInput.title.trim(),
            why: draftInput.why.trim(),
            timeframeLabel: draftInput.timeframeLabel,
            capacityLabel: draftInput.capacityLabel,
            startingPoint: draftInput.startingPoint,
        })
        if (!revision && draftCache.current?.key === draftKey) {
            setDraftPaths(draftCache.current.paths)
            setIsDrafting(false)
            return
        }
        setIsDrafting(true)
        void draftGoalPaths(draftInput, controller.signal, revision ?? undefined)
            .then((result) => {
                if (canceled) return
                const paths = result.status === 'ready' ? result.paths : []
                // A revision is keyed to a note, not to the four goal fields, so it never
                // becomes the cached answer for them.
                if (paths.length > 0 && !revision) draftCache.current = { key: draftKey, paths }
                if (revision && paths.length === 0) {
                    // Keep the plan the note was about; asking for a tweak must not cost it.
                    toast.error(
                        result.status === 'error' && result.error.code === 'unchanged_plan'
                            ? GOAL_DRAFT_UNCHANGED_MESSAGE
                            : 'Could not revise the plan'
                    )
                    return
                }
                setDraftPaths(paths)
                setPlanGeneration((generation) => generation + 1)
            })
            .finally(() => {
                if (!canceled) setIsDrafting(false)
            })
        return () => {
            canceled = true
            controller.abort()
        }
    }, [render, outcome, why, timeframeLabel, capacityLabel, startingPoint, revision])

    // Focus the step's primary field once it has settled in.
    useEffect(() => {
        const t = window.setTimeout(() => {
            if (render === 1) inputRef.current?.focus()
            else if (render === 2) whyRef.current?.focus()
        }, 180)
        return () => window.clearTimeout(t)
    }, [render])

    // Enter advances (steps 1-3). Guard: never past step 4, never while a popover
    // is choosing. preventDefault so a focused textarea will not also insert a newline.
    useEffect(() => {
        const onKey = (e: globalThis.KeyboardEvent) => {
            if (e.key !== 'Enter' || e.shiftKey || e.metaKey || e.ctrlKey) return
            if (render >= 4 || openPopovers.current > 0) return
            e.preventDefault()
            advance()
        }
        // Capture phase: GTDialog stops bubbling at the React root, so a bubbling listener would never fire.
        window.addEventListener('keydown', onKey, true)
        return () => window.removeEventListener('keydown', onKey, true)
    }, [render, advance])

    const trackPopover = (open: boolean) => {
        openPopovers.current = Math.max(0, openPopovers.current + (open ? 1 : -1))
    }

    const renderStep = () => {
        if (render === 4) {
            return (
                <StartingPointPanel
                    title={outcome.trim()}
                    capacityLabel={capacityLabel}
                    initialBaseline={startingPoint?.baseline}
                    initialStartingPoint={startingPoint}
                    onBack={back}
                    onConfirm={(nextStartingPoint) => {
                        const confirmedStartingPoint = {
                            ...nextStartingPoint,
                            commitments: creationCommitmentsWithReuse(nextStartingPoint.commitments),
                        }
                        setStartingPoint(confirmedStartingPoint)
                        setReviewRecurringTemplateIds(
                            confirmedStartingPoint.commitments
                                .filter((commitment) => commitment.reused)
                                .map((commitment) => commitment.id)
                        )
                        goTo(5)
                    }}
                />
            )
        }

        if (render === 5) {
            // Only the first draft takes over the step. A revision runs underneath the plan it
            // is revising, so the user keeps reading it — and keeps their edits if it fails.
            if (isDrafting && !revision) {
                return (
                    <>
                        <GTDialogBody className="gap-6">
                            <PlanDraftingState
                                title="Drafting your plan"
                                subtitle="Turning the goal into first steps."
                            />
                        </GTDialogBody>
                        <GTDialogFooter
                            start={
                                <Button variant="ghost" size="sm" onClick={back} className="text-muted-foreground">
                                    Back
                                </Button>
                            }
                        />
                    </>
                )
            }
            const draftedPath = draftPaths[0]
            const draft = {
                ...buildScaffoldReviewDraft(
                    {
                        title: outcome.trim(),
                        why,
                        timeframeLabel,
                        capacityLabel,
                        startingPoint,
                    },
                    draftPaths
                ),
                recurringTemplateIds: reviewRecurringTemplateIds,
            }
            // The review screen owns the outcome, timeframe and capacity once it is showing, so
            // lift its versions back into the wizard before re-drafting: they are what the planner
            // shapes a plan from, and they have to survive the remount when the revision lands.
            const revise = (feedback: string, edited: TGoalDraft) => {
                if (!draftedPath) return
                const selectedTemplateIds = new Set(edited.recurringTemplateIds ?? [])
                setOutcome(edited.title)
                setTimeframeLabel(edited.timeframeLabel)
                setCapacityLabel(edited.capacityLabel)
                setStartingPoint((current) =>
                    current
                        ? {
                              ...current,
                              commitments: current.commitments.map((commitment) => ({
                                  ...commitment,
                                  reused:
                                      commitment.source === 'recurring'
                                          ? selectedTemplateIds.has(commitment.id)
                                          : commitment.reused,
                              })),
                          }
                        : current
                )
                setReviewRecurringTemplateIds(edited.recurringTemplateIds)
                setRevision({ feedback, currentPlan: { ...draftedPath, items: edited.items } })
            }
            return (
                <ReviewScreen
                    // Keyed on the plan generation so a revised plan replaces the rows the user
                    // was editing, instead of leaving ReviewScreen's own item state stale.
                    key={`${draftedPath?.id ?? 'manual'}-${planGeneration}`}
                    draft={draft}
                    showTypePicker={!draftedPath}
                    onConfirm={createGoal}
                    onRevise={draftedPath ? revise : undefined}
                    isRevising={isDrafting}
                    onBack={back}
                    layout="dialog"
                />
            )
        }

        const canAdvance = render !== 1 || outcome.trim().length > 0

        return (
            <>
                <GTDialogBody className="gap-6">
                    {render === 1 && (
                        <div className="space-y-4">
                            <StepHeading title="What's the outcome?" subtitle="Name the result you're aiming for." />
                            <div
                                style={{ animationDelay: '80ms', animationFillMode: 'both' }}
                                className="space-y-3 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
                            >
                                <Input
                                    ref={inputRef}
                                    value={outcome}
                                    onChange={(e) => setOutcome(e.target.value)}
                                    placeholder="Goal outcome"
                                    aria-label="Goal outcome"
                                    className="h-11 rounded-lg text-body-md"
                                />
                            </div>
                        </div>
                    )}

                    {render === 2 && (
                        <div className="space-y-4">
                            <StepHeading
                                title="Why does this matter?"
                                subtitle="The reason you'll come back to when the week gets loud."
                            />
                            <div
                                style={{ animationDelay: '80ms', animationFillMode: 'both' }}
                                className="space-y-2 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
                            >
                                <textarea
                                    ref={whyRef}
                                    value={why}
                                    onChange={(e) => setWhy(e.target.value)}
                                    placeholder="Why this matters"
                                    rows={3}
                                    aria-label="Why this goal matters"
                                    className="flex w-full resize-none rounded-lg border border-input bg-transparent px-3 py-2.5 text-body-md touch-field-floor leading-relaxed transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                />
                                <p className="text-body-sm text-muted-foreground">
                                    One honest sentence beats a paragraph.
                                </p>
                            </div>
                        </div>
                    )}

                    {render === 3 && (
                        <div className="space-y-4">
                            <StepHeading
                                title="Timeframe & capacity"
                                subtitle="How long you've got, and the pace you can actually keep."
                            />
                            <div
                                style={{ animationDelay: '80ms', animationFillMode: 'both' }}
                                className="flex flex-wrap items-center gap-2 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
                            >
                                <PropertyPill
                                    icon={<CalendarRange className="size-3.5" />}
                                    value={timeframeLabel}
                                    options={TIMEFRAME_OPTIONS}
                                    customDate
                                    onSelect={setTimeframeLabel}
                                    onOpenChange={trackPopover}
                                />
                                <PropertyPill
                                    icon={<Gauge className="size-3.5" />}
                                    value={capacityLabel}
                                    options={CAPACITY_OPTIONS}
                                    units={CAPACITY_UNITS}
                                    unitKey={capacityFromLabel(capacityLabel)?.unit}
                                    onSelect={(next) => {
                                        setCapacityLabel(next)
                                    }}
                                    onOpenChange={trackPopover}
                                />
                            </div>
                        </div>
                    )}
                </GTDialogBody>
                <GTDialogFooter
                    start={
                        render > 1 && (
                            <Button variant="ghost" size="sm" onClick={back} className="text-muted-foreground">
                                Back
                            </Button>
                        )
                    }
                    onConfirm={advance}
                    confirmDisabled={!canAdvance}
                    confirmLabel={
                        <>
                            Continue
                            <ArrowRight className="size-4" />
                        </>
                    }
                />
            </>
        )
    }

    const steps = <GTDialogSteps step={render} total={5} />
    const content = (
        <div
            key={render}
            className={cn(
                'flex min-h-0 flex-col gap-6 transition-[opacity,transform] duration-150 ease-out',
                exiting ? 'translate-y-1 opacity-0' : 'translate-y-0 opacity-100'
            )}
        >
            {renderStep()}
        </div>
    )

    if (isOnboarding) {
        return (
            <div className="flex flex-col gap-6 px-5 py-4">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-label-md text-muted-foreground">New goal</span>
                    {steps}
                </div>
                {content}
            </div>
        )
    }

    return (
        <GTDialog open onOpenChange={(next) => !next && closeFlow()} label="New goal" meta={steps} size="wide">
            {content}
        </GTDialog>
    )
}

export default ScaffoldFlow
export { ScaffoldFlow }
